import { useState } from 'react';
import {
  Eye,
  Download,
  UploadCloud,
  FileText,
  Trash2,
  Calendar,
  User,
  Sparkles,
  ExternalLink,
  Plus,
} from 'lucide-react';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import {
  type ProjectDrawingRow,
  useProjectDrawings,
  useDeleteDrawing,
  formatFileSize,
  triggerFileDownload,
} from '../lib/project-drawings';
import { formatDate, TableSkeleton, ErrorState, useToast } from './ui';
import Confirm from './Confirm';
import DrawingViewerModal from './DrawingViewerModal';
import ElevationUploadDrawer from './ElevationUploadDrawer';

interface Props {
  projectId: string;
  projectName?: string;
}

export default function ProjectElevationsLedger({ projectId, projectName }: Props) {
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const toast = useToast();

  const { data, isLoading, error, refetch } = useProjectDrawings(projectId);

  const [viewingDrawing, setViewingDrawing] = useState<ProjectDrawingRow | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectDrawingRow | null>(null);

  const deleteMutation = useDeleteDrawing(projectId);

  if (isLoading) {
    return (
      <div className="table-wrap" style={{ padding: 20 }}>
        <TableSkeleton rows={3} cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-wrap" style={{ padding: 20 }}>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const elevations = data?.elevations ?? [];

  async function doDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        category: deleteTarget.category,
        targetProjectId: projectId,
      });
      toast.show(`Elevation item "${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not delete elevation item', true);
    }
  }

  function handleDownload(d: ProjectDrawingRow) {
    triggerFileDownload(d.fileUrl, d.fileName, d.fileType);
  }

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              3D Elevations & Architectural Renders
            </h3>
            {elevations.length > 0 && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: '#f0f9ff',
                  color: '#0284c7',
                  border: '1px solid #bae6fd',
                }}
              >
                {elevations.length} {elevations.length === 1 ? 'file' : 'files'}
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-soft)', margin: '4px 0 0' }}>
            Multi-angle 3D renders, exterior perspectives, night views, and architectural elevation drawings.
          </p>
        </div>

        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setUploadOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <UploadCloud size={14} />
          <span>Upload 3D Elevations</span>
        </button>
      </div>

      {/* Elevations Grid or Empty State */}
      {elevations.length === 0 ? (
        <div
          style={{
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius)',
            padding: '48px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            background: 'var(--surface-2)',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: '#f0f9ff',
              color: '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            <Sparkles size={26} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>
            No 3D Elevations uploaded yet
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-soft)', margin: '0 0 16px', maxWidth: 380 }}>
            Upload multiple 3D perspectives, front/side renders, walkthrough images, or elevation drawings for this site.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-auto btn-sm"
            onClick={() => setUploadOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <UploadCloud size={14} />
            <span>Upload 3D Elevations</span>
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {elevations.map((item) => {
            const isImage =
              item.fileType.startsWith('image/') ||
              item.fileUrl.startsWith('data:image/') ||
              /\.(png|jpe?g|webp|gif|svg)$/i.test(item.fileName);
            const isPdf =
              item.fileType === 'application/pdf' ||
              item.fileUrl.startsWith('data:application/pdf') ||
              /\.pdf$/i.test(item.fileName);

            return (
              <div
                key={item.id}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'var(--shadow-xs)',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Visual Preview Box */}
                <div
                  onClick={() => setViewingDrawing(item)}
                  style={{
                    height: 190,
                    background: isImage ? '#0f172a' : '#f8fafc',
                    borderBottom: '1px solid var(--border)',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Click to view fullscreen"
                >
                  {isImage ? (
                    <img
                      src={item.fileUrl}
                      alt={item.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  ) : isPdf ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <FileText size={44} style={{ color: '#dc2626', marginBottom: 8 }} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                        PDF Elevation Document
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
                        {item.fileName}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <FileText size={44} style={{ color: 'var(--text-soft)', marginBottom: 8 }} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                        {item.fileName}
                      </div>
                    </div>
                  )}

                  {/* Hover Eye Overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(15, 23, 42, 0.45)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 13,
                      opacity: 0.9,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <Eye size={18} />
                    <span>Click to View</span>
                  </div>
                </div>

                {/* Details Body */}
                <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: 'var(--text)',
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.title}
                  </div>

                  <div
                    style={{
                      fontSize: 11.5,
                      color: 'var(--text-soft)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <span>{formatDate(item.createdAt)}</span>
                    <span>•</span>
                    <span>{formatFileSize(item.fileSize)}</span>
                    {item.uploadedByName && (
                      <>
                        <span>•</span>
                        <span>{item.uploadedByName}</span>
                      </>
                    )}
                  </div>

                  {item.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text)',
                        background: 'var(--surface-2)',
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid var(--border-subtle)',
                        lineHeight: 1.4,
                        marginBottom: 10,
                      }}
                    >
                      {item.description}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginTop: 'auto',
                      paddingTop: 10,
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-auto btn-sm btn-ghost"
                      onClick={() => setViewingDrawing(item)}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        fontWeight: 600,
                      }}
                    >
                      <Eye size={14} />
                      <span>View</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-auto btn-sm btn-ghost"
                      onClick={() => handleDownload(item)}
                      title="Download elevation file"
                    >
                      <Download size={14} />
                    </button>

                    {admin && (
                      <button
                        type="button"
                        className="icon-btn btn-danger"
                        onClick={() => setDeleteTarget(item)}
                        title="Delete elevation file"
                        style={{ width: 30, height: 30 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Drawer */}
      {uploadOpen && (
        <ElevationUploadDrawer
          projectId={projectId}
          projectName={projectName}
          onClose={() => setUploadOpen(false)}
        />
      )}

      {/* Fullscreen Viewer Modal */}
      {viewingDrawing && (
        <DrawingViewerModal
          initialDrawing={viewingDrawing}
          allVersions={elevations}
          projectName={projectName}
          onClose={() => setViewingDrawing(null)}
          onUploadNewRevision={() => setUploadOpen(true)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <Confirm
          title={`Delete "${deleteTarget.title}"?`}
          message="Are you sure you want to remove this 3D elevation file? It will be removed from the project elevation gallery."
          busy={deleteMutation.isPending}
          onConfirm={() => void doDelete()}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
